// frontend/src/pages/user/CheckoutPage.js
import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import UserLayout from '../../components/layouts/UserLayout';
import { AuthContext } from '../../contexts/AuthContext';
import cartService from '../../services/cartService';
import orderService from '../../services/orderService';
import GooglePlacesAutocomplete from '../../components/GooglePlacesAutocomplete';

const CheckoutPage = () => {
  const { userAuth } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  
  const [shippingAddress, setShippingAddress] = useState({
    address: '',
    city: '',
    postalCode: '',
    country: 'India',
    phone: userAuth.user?.mobileNumber || ''
  });
  
  const [addressInputMode, setAddressInputMode] = useState('manual'); // 'manual', 'saved', 'current'
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState('');

  useEffect(() => {
    fetchCart();
    loadUserLocation();
  }, []);

  const fetchCart = async () => {
    setLoading(true);
    try {
      const response = await cartService.getCart();
      if (response.success) {
        if (!response.data.items || response.data.items.length === 0) {
          toast.error('Your cart is empty');
          navigate('/user/cart');
          return;
        }
        setCart(response.data);
      } else {
        toast.error(response.message || 'Failed to fetch cart');
        navigate('/user/cart');
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
      toast.error('Something went wrong');
      navigate('/user/cart');
    } finally {
      setLoading(false);
    }
  };

  const loadUserLocation = () => {
    if (userAuth.user?.location?.address) {
      // Don't auto-load saved address, let user choose
      console.log('💾 User has saved address available');
    }
  };

  // 🎯 NEW: High-accuracy geolocation function
  const getCurrentLocation = async () => {
    setLocationLoading(true);
    setLocationError('');
    
    try {
      console.log('📍 Starting high-accuracy location detection...');
      
      // Check if geolocation is supported
      if (!navigator.geolocation) {
        throw new Error('Geolocation is not supported by this browser');
      }

      // Request location with high accuracy settings
      const position = await new Promise((resolve, reject) => {
        const options = {
          enableHighAccuracy: true, // Use GPS for high accuracy
          timeout: 15000,           // 15 second timeout
          maximumAge: 0            // Don't use cached location
        };

        navigator.geolocation.getCurrentPosition(
          (position) => {
            console.log('✅ Location obtained:', {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy + ' meters'
            });
            resolve(position);
          },
          (error) => {
            console.error('❌ Geolocation error:', error);
            let errorMessage = 'Unable to get your location';
            
            switch (error.code) {
              case error.PERMISSION_DENIED:
                errorMessage = 'Location access denied. Please enable location permissions.';
                break;
              case error.POSITION_UNAVAILABLE:
                errorMessage = 'Location information unavailable. Please try again.';
                break;
              case error.TIMEOUT:
                errorMessage = 'Location request timed out. Please try again.';
                break;
              default:
                errorMessage = 'Unable to get your location. Please try again.';
            }
            
            reject(new Error(errorMessage));
          },
          options
        );
      });

      const { latitude, longitude } = position.coords;
      console.log('🔄 Converting coordinates to address...');

      // Reverse geocode server-side (see reverseGeocode below for why)
      const detected = await reverseGeocode(latitude, longitude);

      // Keep whatever the buyer already typed for any field the lookup could not
      // fill. The OpenStreetMap fallback in particular returns no postal code —
      // its PIN data put a Surat coordinate in Rajasthan — so that field is left
      // for the buyer rather than filled in wrongly.
      setShippingAddress(prev => ({
        ...prev,
        address: detected.address || prev.address,
        city: detected.city || prev.city,
        postalCode: detected.postalCode || prev.postalCode
      }));

      console.log('✅ Address updated successfully:', detected);
      toast.success('Current location detected successfully!');
      
    } catch (error) {
      console.error('❌ Location error:', error);
      setLocationError(error.message);
      toast.error(error.message);
    } finally {
      setLocationLoading(false);
    }
  };

  // Reverse geocoding goes through our own API, never straight to Google.
  //
  // This used to fetch maps.googleapis.com/maps/api/geocode/json with the
  // browser key from config/googleMaps.js. That key is HTTP-referrer restricted,
  // which is correct for the Maps JavaScript and Places APIs but is rejected
  // outright by the Geocoding *web service*:
  //
  //   REQUEST_DENIED — API keys with referer restrictions cannot be used with this API.
  //
  // So every single lookup failed and the buyer saw "Failed to convert location
  // to address". No key swap fixes it from the browser; the Geocoding web
  // service has to be called server-side. /api/location/reverse-geocode does
  // that, and falls back to OpenStreetMap when Google is unavailable.
  const reverseGeocode = async (latitude, longitude) => {
    try {
      const base = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(
        `${base}/location/reverse-geocode?lat=${encodeURIComponent(latitude)}&lng=${encodeURIComponent(longitude)}`
      );

      if (!response.ok) {
        throw new Error('Failed to get address from coordinates');
      }

      const payload = await response.json();
      console.log('🗺️ Geocoding result:', payload);

      if (!payload?.success || !payload?.data?.address) {
        throw new Error('No address found for current location');
      }

      return payload.data;
    } catch (error) {
      console.error('❌ Reverse geocoding error:', error);
      throw new Error('Failed to convert location to address');
    }
  };

  // parseGoogleAddress() used to live here. It unpacked Google's
  // address_components, which the server now does — /api/location/reverse-geocode
  // returns address, city and postalCode ready to use.

  // 🎯 ENHANCED: Handle address input mode changes
  const handleAddressInputModeChange = async (mode) => {
    setAddressInputMode(mode);
    setLocationError('');
    
    if (mode === 'saved' && userAuth.user?.location?.address) {
      // Use saved address from user profile
      setShippingAddress(prev => ({
        ...prev,
        address: userAuth.user.location.address
      }));
      toast.success('Saved address loaded');
      
    } else if (mode === 'current') {
      // Get current location
      await getCurrentLocation();
      
    } else if (mode === 'manual') {
      // Clear address for manual entry
      setShippingAddress(prev => ({
        ...prev,
        address: ''
      }));
    }
  };

  // 🎯 NEW: Handle Google Places selection for manual mode
  const handlePlaceSelected = (placeData) => {
    console.log('🏠 Place selected:', placeData);
    
    setShippingAddress(prev => ({
      ...prev,
      address: placeData.address
    }));
    
    // Try to extract city and postal code from the address
    if (placeData.address) {
      // Simple parsing for Indian addresses
      const addressParts = placeData.address.split(',').map(part => part.trim());
      
      // Look for postal code (6 digits)
      const postalCodeMatch = placeData.address.match(/\b\d{6}\b/);
      if (postalCodeMatch) {
        setShippingAddress(current => ({
          ...current,
          postalCode: postalCodeMatch[0]
        }));
      }
      
      // Try to extract city (usually after the first comma)
      if (addressParts.length > 1) {
        const potentialCity = addressParts[addressParts.length - 3] || addressParts[1];
        if (potentialCity && potentialCity.length > 2) {
          setShippingAddress(current => ({
            ...current,
            city: potentialCity
          }));
        }
      }
    }
  };

  const calculateTotals = () => {
    // Item prices already include the Zammer Shipping Charge (baked in server-side).
    // Buyer pays ONLY that — no separate 18% tax, no ₹50 order fee.
    const subtotal = cart.items.reduce((total, item) => {
      return total + (item.price * item.quantity);
    }, 0);

    return {
      subtotal: Math.round(subtotal),
      taxPrice: 0,
      shippingPrice: 0,
      totalPrice: Math.round(subtotal)
    };
  };

  const validateForm = () => {
    if (!shippingAddress.address.trim()) {
      toast.error('Please enter your address');
      return false;
    }
    if (!shippingAddress.city.trim()) {
      toast.error('Please enter your city');
      return false;
    }
    if (!shippingAddress.postalCode.trim()) {
      toast.error('Please enter postal code');
      return false;
    }
    if (!shippingAddress.phone.trim()) {
      toast.error('Please enter phone number');
      return false;
    }
    return true;
  };

  const handlePlaceOrder = async () => {
    if (!validateForm()) return;

    setProcessing(true);
    
    try {
      const totals = calculateTotals();
      
      // Format order data (paymentMethod will be selected on the payment page)
      const orderData = orderService.formatOrderForAPI(
        cart.items,
        shippingAddress,
        'pending'
      );

      // Navigate to payment page with order data
      navigate('/user/payment', {
        state: {
          orderData,
          totals,
          cartItems: cart.items
        }
      });
      
    } catch (error) {
      console.error('Error processing order:', error);
      toast.error('Something went wrong while processing your order');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <UserLayout>
        <div className="container mx-auto p-4">
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading checkout...</p>
            </div>
          </div>
        </div>
      </UserLayout>
    );
  }

  const totals = calculateTotals();

  return (
    <UserLayout>
      <div className="container mx-auto p-4 max-w-6xl">
        {/* Header */}
        <div className="flex items-center mb-6">
          <button onClick={() => navigate('/user/cart')} className="mr-4">
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-2xl font-bold text-gray-800">Checkout</h1>
        </div>

        {/* Single column throughout, same as CartPage. The buyer app renders
            inside MobileContainer's 480px frame, but lg: breakpoints fire off
            the viewport width — so on any desktop browser this became three
            columns inside 480px and the Order Summary got 128px, which ran
            "Shipping"/"Included" together and wrapped the payment button onto
            three lines. */}
        <div className="space-y-6">
          {/* Checkout Form */}
          <div className="space-y-6">
            {/* Shipping Address */}
            {/* data-clarity-mask: Clarity masks <input> values by default, but a saved address rendered
                as plain text is not an input and would otherwise be fully readable in session
                recordings. Mask the whole block rather than trusting field-level rules. */}
            <div className="bg-white rounded-lg shadow-sm border p-6" data-clarity-mask="true">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Shipping Address</h2>
              
              {/* 🎯 NEW: Address Input Mode Selection */}
              <div className="mb-6">
                <p className="text-sm font-medium text-gray-700 mb-3">Choose address option:</p>
                {/* Stacked, not md:grid-cols-3. Each option is radio + icon +
                    label — about 170px of content that cannot shrink — and at
                    three across inside the 480px frame they were 71px wide, so
                    "Use Current Location" broke mid-word and the radios spilled
                    over the neighbouring card. */}
                <div className="space-y-3">
                  {/* Current Location Option */}
                  <label className="flex items-center p-3 border-2 rounded-lg cursor-pointer hover:bg-orange-50 transition-colors">
                    <input
                      type="radio"
                      name="addressMode"
                      value="current"
                      checked={addressInputMode === 'current'}
                      onChange={(e) => handleAddressInputModeChange(e.target.value)}
                      className="mr-3 text-orange-600"
                      disabled={locationLoading}
                    />
                    <div className="flex items-center">
                      {locationLoading ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-500 mr-2"></div>
                      ) : (
                        <svg className="w-5 h-5 text-orange-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      )}
                      <span className="text-sm font-medium">
                        {locationLoading ? 'Detecting...' : 'Use Current Location'}
                      </span>
                    </div>
                  </label>

                  {/* Saved Address Option */}
                  {userAuth.user?.location?.address && (
                    <label className="flex items-center p-3 border-2 rounded-lg cursor-pointer hover:bg-green-50 transition-colors">
                      <input
                        type="radio"
                        name="addressMode"
                        value="saved"
                        checked={addressInputMode === 'saved'}
                        onChange={(e) => handleAddressInputModeChange(e.target.value)}
                        className="mr-3 text-green-600"
                      />
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                        </svg>
                        <span className="text-sm font-medium">Use Saved Address</span>
                      </div>
                    </label>
                  )}

                  {/* Manual Entry Option */}
                  <label className="flex items-center p-3 border-2 rounded-lg cursor-pointer hover:bg-orange-50 transition-colors">
                    <input
                      type="radio"
                      name="addressMode"
                      value="manual"
                      checked={addressInputMode === 'manual'}
                      onChange={(e) => handleAddressInputModeChange(e.target.value)}
                      className="mr-3 text-orange-600"
                    />
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-orange-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                      <span className="text-sm font-medium">Type Address</span>
                    </div>
                  </label>
                </div>

                {/* Location Error Display */}
                {locationError && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm text-red-700">{locationError}</p>
                    </div>
                    <button
                      onClick={() => handleAddressInputModeChange('current')}
                      className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
                    >
                      Try again
                    </button>
                  </div>
                )}
              </div>
              
              {/* Full-width fields for the same reason: md:grid-cols-2 halved
                  City and Postal Code to ~130px inside the frame. */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Street Address *
                  </label>
                  
                  {/* 🎯 ENHANCED: Conditional Address Input */}
                  {addressInputMode === 'manual' ? (
                    <GooglePlacesAutocomplete
                      value={shippingAddress.address}
                      onChange={(address) => setShippingAddress({...shippingAddress, address})}
                      onPlaceSelected={handlePlaceSelected}
                      placeholder="Start typing your address..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  ) : (
                    <textarea
                      value={shippingAddress.address}
                      onChange={(e) => setShippingAddress({...shippingAddress, address: e.target.value})}
                      placeholder={
                        addressInputMode === 'current' 
                          ? 'Address will be detected automatically...' 
                          : 'Your saved address will appear here...'
                      }
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-50"
                      readOnly={addressInputMode !== 'manual'}
                      required
                    />
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City *
                  </label>
                  <input
                    type="text"
                    value={shippingAddress.city}
                    onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
                    placeholder="Enter city"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Postal Code *
                  </label>
                  <input
                    type="text"
                    value={shippingAddress.postalCode}
                    onChange={(e) => setShippingAddress({...shippingAddress, postalCode: e.target.value})}
                    placeholder="Enter postal code"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country
                  </label>
                  <select
                    value={shippingAddress.country}
                    onChange={(e) => setShippingAddress({...shippingAddress, country: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="India">India</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={shippingAddress.phone}
                    onChange={(e) => setShippingAddress({...shippingAddress, phone: e.target.value})}
                    placeholder="Enter phone number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Order Items Preview */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Order Items ({cart.items.length})</h2>
              
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {cart.items.map((item) => (
                  <div key={item._id} className="flex items-center space-x-3 p-2 border-b border-gray-100 last:border-b-0">
                    <div className="w-12 h-12 bg-gray-200 rounded-md overflow-hidden flex-shrink-0">
                      {item.product.images && item.product.images[0] ? (
                        <img
                          src={item.product.images[0]}
                          alt={item.product.name}
                          className="w-full h-full object-cover object-top"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 line-clamp-1">{item.product.name}</p>
                      <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-800">₹{item.price * item.quantity}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div>
            <div className="bg-white rounded-lg shadow-sm border sticky top-4">
              <div className="p-4 border-b">
                <h2 className="text-lg font-semibold text-gray-800">Order Summary</h2>
              </div>
              
              {/* gap-2 on every row: justify-between alone leaves no minimum
                  space, so a narrow column rendered these as "ShippingIncluded"
                  and "Total₹2159". flex-wrap drops the value to its own line
                  rather than letting it touch the label again. */}
              <div className="p-4 space-y-3">
                <div className="flex justify-between flex-wrap gap-2 text-sm">
                  <span className="text-gray-600">Subtotal ({cart.items.length} items)</span>
                  <span className="font-medium">₹{totals.subtotal}</span>
                </div>

                <div className="flex justify-between flex-wrap gap-2 text-sm">
                  <span className="text-gray-600">Shipping</span>
                  <span className="font-medium text-green-600">Included</span>
                </div>

                <div className="border-t pt-3">
                  <div className="flex justify-between flex-wrap gap-2 font-bold text-lg">
                    <span>Total</span>
                    <span className="text-orange-600">₹{totals.totalPrice}</span>
                  </div>
                </div>
              </div>
              
              <div className="p-4 border-t">
                <button
                  onClick={handlePlaceOrder}
                  disabled={processing || locationLoading}
                  className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center"
                >
                  {processing ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      Proceed to Payment
                    </>
                  )}
                </button>
                
                <Link
                  to="/user/cart"
                  className="block w-full text-center text-orange-600 hover:text-orange-700 py-2 mt-3 font-medium"
                >
                  Back to Cart
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </UserLayout>
  );
};

export default CheckoutPage;